import { adminEmailsApi } from '@/api/admin/adminEmailsApi';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ConfirmDialogAction } from '@/components/admin/ConfirmDialogAction';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { handleGlobalError } from '@/utils/error-handler';
import { useCallback, useEffect, useRef, useState } from 'react';

const AdminEmailTemplatesPage = () => {
  const { toast } = useToast();
  const [names, setNames] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [content, setContent] = useState('');
  // "Not here yet", "came back empty" and "could not be read" are three
  // different claims, and `content || 'Cargando...'` made all three read as the
  // first. A failed fetch left the panel saying "Cargando..." forever, next to
  // a Eliminar button whose dialog only asks for the name — which is still in
  // the header. That is a template deleted without its body ever being seen.
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [templateFailed, setTemplateFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const selectionId = useRef(0);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setNames(await adminEmailsApi.listNames());
    } catch (error) {
      handleGlobalError(error, 'No se pudieron cargar los templates', 'Revisá la conexión e intentá de nuevo.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const select = async (name: string) => {
    // Clicking two templates quickly leaves two fetches in flight. Only the newest
    // may write: otherwise the panel shows one template's body under another's
    // name, next to a delete button that acts on the name.
    const current = ++selectionId.current;
    setSelected(name);
    setContent('');
    setTemplateFailed(false);
    setIsLoadingTemplate(true);
    try {
      const body = await adminEmailsApi.getTemplate(name);
      if (current !== selectionId.current) return;
      setContent(body);
    } catch (error) {
      if (current !== selectionId.current) return;
      setTemplateFailed(true);
      handleGlobalError(error, 'No se pudo leer el template', 'Revisá la conexión e intentá de nuevo.');
    } finally {
      if (current === selectionId.current) {
        setIsLoadingTemplate(false);
      }
    }
  };

  const remove = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await adminEmailsApi.deleteTemplate(pendingDelete);
      toast({ title: `${pendingDelete} eliminado` });
      if (selected === pendingDelete) {
        setSelected(null);
        setContent('');
        setTemplateFailed(false);
        setIsLoadingTemplate(false);
      }
      await load();
    } catch (error) {
      handleGlobalError(error, 'No se pudo eliminar el template', 'Revisá la auditoría para ver el detalle.');
    } finally {
      setIsDeleting(false);
      setPendingDelete(null);
      setConfirmation('');
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Templates de email</h1>
        <p className="text-sm text-gray-500">
          El copy de los emails vive en AWS SES, no en el repositorio.
        </p>
      </div>

      {isLoading ? (
        <Skeleton className="h-48" />
      ) : (
        <div className="grid gap-4 md:grid-cols-[280px_1fr]">
          <Card className="max-h-[70vh] overflow-y-auto p-2">
            {names.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => select(name)}
                className={`block w-full rounded px-3 py-2 text-left text-sm ${
                  selected === name
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {name}
              </button>
            ))}
          </Card>

          <Card className="p-4">
            {selected === null ? (
              <p className="text-sm text-gray-500">
                Elegí un template de la lista para ver su contenido.
              </p>
            ) : (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="font-medium text-gray-900">{selected}</h2>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setPendingDelete(selected)}
                  >
                    Eliminar
                  </Button>
                </div>
                {isLoadingTemplate ? (
                  <Skeleton className="h-48" />
                ) : templateFailed ? (
                  <p className="text-sm text-gray-500">
                    No se pudo leer el contenido de este template. Volvé a
                    seleccionarlo para reintentar.
                  </p>
                ) : content === '' ? (
                  <p className="text-sm text-gray-500">
                    El template está vacío.
                  </p>
                ) : (
                  <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-3 text-xs text-gray-800">
                    {content}
                  </pre>
                )}
              </>
            )}
          </Card>
        </div>
      )}

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
            setConfirmation('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar {pendingDelete}</AlertDialogTitle>
            <AlertDialogDescription>
              Borrar un template rompe el envío del email que lo usa. Escribí el
              nombre exacto para confirmar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            placeholder={pendingDelete ?? ''}
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <ConfirmDialogAction
              onConfirm={remove}
              isRunning={isDeleting}
              runningLabel="Eliminando..."
              disabled={confirmation !== pendingDelete}
            >
              Eliminar
            </ConfirmDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminEmailTemplatesPage;
